import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";

function formatDateLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

export default function RescheduleModal({ appointment, open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/doctors/${appointment.doctor_id}`)
      .then((r) => {
        setData(r.data);
        setActiveDate(r.data.dates[0]);
      })
      .finally(() => setLoading(false));
  }, [open, appointment.doctor_id]);

  const slotsForDate = (data?.slots_by_date?.[activeDate] || []).filter(
    (s) => s.mode_allowed.includes(appointment.mode) && s.id !== appointment.slot_id,
  );

  const submit = async () => {
    if (!selectedSlot) {
      toast.error("Please pick a new slot");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/appointments/${appointment.id}/reschedule`, {
        new_slot_id: selectedSlot.id,
      });
      toast.success("Appointment rescheduled");
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        data-testid={`reschedule-modal-${appointment.id}`}
        className="max-w-lg rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle>Reschedule appointment</DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="h-48 bg-muted rounded-xl animate-pulse" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="flex gap-2 pb-2">
                {data.dates.map((d) => {
                  const isActive = d === activeDate;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        setActiveDate(d);
                        setSelectedSlot(null);
                      }}
                      data-testid={`reschedule-date-${d}`}
                      className={
                        "shrink-0 px-3 py-2 rounded-xl border transition-all min-w-[90px] text-xs " +
                        (isActive
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-white hover:border-primary/40")
                      }
                    >
                      <div className="font-medium">{formatDateLabel(d)}</div>
                      <div className="text-[11px] opacity-80 mt-0.5">
                        {new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3">
              {slotsForDate.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-secondary/60 rounded-xl p-4">
                  No {appointment.mode} slots available on this date.
                </div>
              ) : (
                <div
                  data-testid="reschedule-slot-grid"
                  className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto"
                >
                  {slotsForDate.map((s) => {
                    const disabled = s.is_booked;
                    const selected = selectedSlot?.id === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => setSelectedSlot(s)}
                        data-testid={`reschedule-slot-${s.id}`}
                        className={
                          "h-11 rounded-xl text-sm font-medium border transition-all " +
                          (disabled
                            ? "border-border bg-muted text-muted-foreground line-through cursor-not-allowed"
                            : selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-white hover:border-primary/40")
                        }
                      >
                        {s.start_time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-2 flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={onClose} className="rounded-full">
                Cancel
              </Button>
              <Button
                data-testid="reschedule-confirm-btn"
                onClick={submit}
                disabled={!selectedSlot || busy}
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
              >
                {busy ? "Saving…" : "Confirm reschedule"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
